/**
 * LiveKit-backed voice engine session. The only voice engine session
 * — the hand-rolled Membrane offer/answer/ICE pipeline was retired in
 * Phase 8 of the spatial-audio migration.
 *
 * See [docs/guides/spatial-audio-livekit.md](../../../../docs/guides/spatial-audio-livekit.md).
 */

import {
  Room,
  RoomEvent,
  Track,
  type LocalParticipant,
  type RemoteParticipant,
  type RemoteTrack,
  type RemoteTrackPublication,
} from "livekit-client";

export interface LiveKitVoiceEngineOptions {
  /** Public LiveKit signaling URL (`wss://app.example.com/livekit`). */
  wsUrl: string;
  /** Short-lived JWT minted by `POST /api/v1/voice/token`. */
  token: string;
  /** Mic capture stream from `MicDiscovery.openMic`. */
  localStream: MediaStream;
  /**
   * Called when a remote audio track lands. The callback is responsible
   * for routing the track through `SpatialAudioController` (PannerNode
   * + distance gate). `publication.trackName === "music"` distinguishes
   * the jukebox-host stream from voice.
   */
  onRemoteTrack?: (
    participantIdentity: string,
    track: RemoteTrack,
    publication: RemoteTrackPublication,
  ) => void;
  /** Called when a remote track unpublishes (PTT release, music stop). */
  onRemoteTrackEnded?: (
    participantIdentity: string,
    publication: RemoteTrackPublication,
  ) => void;
  /** Called when a participant leaves the Room entirely. */
  onParticipantLeft?: (identity: string) => void;
  onError?: (err: Error) => void;
}

export class LiveKitVoiceEngineSession {
  private readonly room: Room;
  private readonly opts: LiveKitVoiceEngineOptions;
  private currentToken: string;
  private connected = false;
  private micPublishedTrackSid: string | null = null;

  constructor(opts: LiveKitVoiceEngineOptions) {
    this.opts = opts;
    this.currentToken = opts.token;
    this.room = new Room({
      adaptiveStream: true,
      dynacast: true,
      stopLocalTrackOnUnpublish: false,
    });

    this.wireRoomEvents();
  }

  async connect(): Promise<void> {
    if (this.connected) return;
    try {
      console.log(`[voice] room.connect: ${this.opts.wsUrl}`);
      await this.room.connect(this.opts.wsUrl, this.currentToken);
      this.connected = true;
      console.log(`[voice] room.connected sid=${this.room.localParticipant.identity}`);
    } catch (err) {
      console.warn("[voice] room.connect failed:", err);
      this.opts.onError?.(err instanceof Error ? err : new Error(String(err)));
      throw err;
    }
  }

  /**
   * Rotate the session token without tearing down state outside the
   * Room. Disconnects + reconnects with the new JWT — listeners will
   * experience a ~1-second audio gap while the signaling reconnect
   * completes, but the surrounding `VoiceConnection` (channel join,
   * SpatialAudioController, mic track) stays intact.
   */
  async reconnect(newToken: string): Promise<void> {
    this.currentToken = newToken;
    if (!this.connected) {
      await this.connect();
      return;
    }
    await this.disconnect();
    await this.connect();
  }

  /**
   * Publish the user's mic. Called on PTT press. Idempotent — second
   * call while a track is already published is a no-op.
   */
  async startPtt(): Promise<void> {
    if (!this.connected) await this.connect();
    if (this.micPublishedTrackSid) return;

    const [audioTrack] = this.opts.localStream.getAudioTracks();
    if (!audioTrack) throw new Error("LiveKitVoiceEngineSession: no audio track in localStream");

    const publication = await this.room.localParticipant.publishTrack(audioTrack, {
      source: Track.Source.Microphone,
    });

    this.micPublishedTrackSid = publication.trackSid;
    console.log(`[voice] mic published sid=${publication.trackSid}`);
  }

  /** Unpublish the mic on PTT release. Tolerates double-stop. */
  async stopPtt(): Promise<void> {
    if (!this.micPublishedTrackSid) return;

    const local: LocalParticipant = this.room.localParticipant;
    const pub = local.getTrackPublication(Track.Source.Microphone);
    if (pub?.track) {
      await local.unpublishTrack(pub.track);
      console.log(`[voice] mic unpublished sid=${this.micPublishedTrackSid}`);
    }

    this.micPublishedTrackSid = null;
  }

  /** Underlying Room — exposed so the music broadcaster can publish on the same connection. */
  getRoom(): Room {
    return this.room;
  }

  async disconnect(): Promise<void> {
    if (!this.connected) return;
    await this.stopPtt().catch(() => {});
    await this.room.disconnect();
    this.connected = false;
    console.log("[voice] room.disconnected");
  }

  private wireRoomEvents() {
    this.room.on(RoomEvent.TrackSubscribed, (track, publication, participant: RemoteParticipant) => {
      if (track.kind !== Track.Kind.Audio) return;
      this.opts.onRemoteTrack?.(participant.identity, track, publication);
    });

    this.room.on(RoomEvent.TrackUnsubscribed, (_track, publication, participant) => {
      this.opts.onRemoteTrackEnded?.(participant.identity, publication);
    });

    this.room.on(RoomEvent.ParticipantDisconnected, (participant) => {
      this.opts.onParticipantLeft?.(participant.identity);
    });

    this.room.on(RoomEvent.Disconnected, () => {
      this.connected = false;
      this.micPublishedTrackSid = null;
    });
  }
}
